import type { ReactNode } from 'react';

/**
 * Error-state primitive · per Stage C §3.
 * ⚠ glyph + title + body + retry primary + optional support secondary.
 *
 * Used by:
 * - Page-level fetch failures (Board · Ticket render error)
 * - Inline form errors handled separately by `<InlineFormError/>` 3.7 phase
 */

export interface ErrorStateProps {
  /** Title line · default «Что-то пошло не так». */
  title?: ReactNode;
  /** Body line · `--ink-soft` · error-specific copy. */
  body?: ReactNode;
  /** Primary retry action · usually re-fires the failed fetch. */
  retry?: { label?: string; onClick: () => void };
  /** Optional secondary action · «Связаться с поддержкой» · etc. */
  secondary?: { label: string; onClick: () => void };
  /** Compact variant for embedded contexts. */
  compact?: boolean;
  className?: string;
}

export function ErrorState({
  title = 'Что-то пошло не так',
  body,
  retry,
  secondary,
  compact = false,
  className,
}: ErrorStateProps) {
  return (
    <div
      className={`error-state${compact ? ' error-state--compact' : ''}${className ? ` ${className}` : ''}`}
      role="alert"
    >
      <div className="error-state__glyph" aria-hidden="true">
        ⚠
      </div>
      <h3 className="error-state__title">{title}</h3>
      {body && <p className="error-state__body">{body}</p>}
      <div className="error-state__actions">
        {retry && (
          <button type="button" className="btn btn--primary" onClick={retry.onClick}>
            {retry.label ?? 'Попробовать снова'}
          </button>
        )}
        {secondary && (
          <button type="button" className="btn btn--ghost" onClick={secondary.onClick}>
            {secondary.label}
          </button>
        )}
      </div>
    </div>
  );
}
